import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

// ─────────────────────────────────────────────────────────────────
// Portal — render an overlay into <body> instead of where it is declared
//
// Several overlays in this app are opened from the sidebar, which is an
// `<aside>` carrying `backdrop-blur-xl` and `translate-x-0` /
// `-translate-x-full`. **A transform or a backdrop filter makes an element the
// containing block for its `position: fixed` descendants** — so an overlay
// declared inside the rail and positioned `fixed inset-0` is laid out inside
// the rail (248px wide) rather than the viewport: a squeezed column of text,
// which is what "the modal is confined to the sidebar" looks like.
//
// Portalling to `<body>` removes the ancestor from the picture entirely, which
// is the only robust fix: `fixed` alone cannot escape a containing block, and
// relying on "no ancestor happens to set a transform" would break again the
// next time the sidebar is animated.
//
// React keeps the component tree intact across a portal — context, state and
// event bubbling all behave as if the overlay were still declared in place,
// so a caller changes nothing but the element this returns renders under.
// ─────────────────────────────────────────────────────────────────

export function Portal({ children }: { children: ReactNode }) {
  // Static rendering (markup tests, any future SSR) has no document to portal
  // into; an overlay that only opens on a click can safely render nothing.
  if (typeof document === 'undefined') return null;
  return createPortal(children, document.body);
}
